import { useState } from 'react';
import { useLabels } from '../config/useLabels';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getAllSchools, grades } from '../config/schools';
import { sanitizeRoomNumberInput, toStoredRoomNumber } from '../types/roomNumber';
import { trackEvent } from '../utils/analytics';
import { getErrorMessage } from '../utils/errorMessage';
import { Mail, Users } from 'lucide-react';
import { useBrandProfile } from '../contexts/brand-profile';
import { brandProfileOf } from '../config/brandProfiles';

interface Student {
  id?: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  metadata?: {
    school?: string;
    grade?: string;
    room_number?: string;
    interested_programs?: string[];
    // Other keys (e.g. bhcs_waiver) must be preserved on update.
    [key: string]: unknown;
  };
}

interface ChildFormProps {
  onClose: () => void;
  student?: Student;
  isEdit?: boolean;
}

type InviteStep =
  | { tag: 'idle' }
  | { tag: 'sending' }
  | { tag: 'done'; resultType: 'access_granted' | 'invite_sent' }
  | { tag: 'error'; message: string };

interface InviteResult {
  type: 'access_granted' | 'invite_sent';
}

interface FnError {
  context?: { json?: () => Promise<{ message?: string }> };
  message?: string;
}

export function ChildForm({ onClose, student, isEdit = false }: ChildFormProps) {
  const { user } = useAuth();
  const t = useLabels();
  // What this brand asks a family. Admin surfaces render this form outside
  // BrandProfileProvider and so get the default brand, i.e. the questions BHCS always asked.
  const { activeBrand } = useBrandProfile();
  const intake = brandProfileOf(activeBrand).intake;
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [programsError, setProgramsError] = useState(false);

  // Invite step (shown after successful student creation)
  const [inviteStep, setInviteStep] = useState(false);
  const [createdStudentId, setCreatedStudentId] = useState<string | null>(null);
  const [createdStudentFirstName, setCreatedStudentFirstName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState<InviteStep>({ tag: 'idle' });

  const [formData, setFormData] = useState({
    firstName: student?.first_name || '',
    lastName: student?.last_name || '',
    dateOfBirth: student?.date_of_birth || '',
    relationship: 'Parent',
    school: student?.metadata?.school || '',
    grade: student?.metadata?.grade || '',
    roomNumber: student?.metadata?.room_number || '',
    interestedPrograms: student?.metadata?.interested_programs || []
  });

  // Get the list of schools from our config
  const schoolsList = getAllSchools();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setProgramsError(false);

    try {
      if (!user?.email) throw new Error('No authenticated user');

      // Only required where the brand asks for them. Kaleido does not, and validating a
      // hidden field would leave the family with an error pointing at nothing.
      if (intake.collectsSchoolAndGrade) {
        if (!formData.school) {
          setError('Please select your child\'s school.');
          setIsSubmitting(false);
          return;
        }

        if (!formData.grade) {
          setError('Please select your child\'s grade.');
          setIsSubmitting(false);
          return;
        }
      }

      // Require at least one interested program ONLY when creating a new student
      if (
        !isEdit &&
        (!formData.interestedPrograms || formData.interestedPrograms.length === 0)
      ) {
        setProgramsError(true);
        setIsSubmitting(false);
        return;
      }

      if (isEdit && student?.id) {
        // Update existing student
        const { error: updateError } = await supabase
          .from('students')
          .update({
            first_name: formData.firstName,
            last_name: formData.lastName,
            date_of_birth: formData.dateOfBirth,
            metadata: {
              // Preserve existing metadata (e.g. bhcs_waiver) — this column is a
              // single jsonb blob, so a bare object would wipe unlisted keys.
              ...(student?.metadata ?? {}),
              school: formData.school,
              grade: formData.grade,
              room_number: toStoredRoomNumber(formData.roomNumber),
              interested_programs: formData.interestedPrograms
            }
          })
          .eq('id', student.id);

        if (updateError) throw updateError;

        // The admin edit path has logged `student_update` since it existed; the parent's own
        // edit of the same fields did not, so a changed grade or school had no attributable
        // author.
        trackEvent({
          eventType: 'student_update',
          user: user,
          userType: 'parent',
          entityType: 'student',
          entityId: student.id,
          metadata: {
            student_name: `${formData.firstName} ${formData.lastName}`.trim(),
            student_school: formData.school,
            student_grade: formData.grade,
            source: 'parent_add_child_form',
          },
        });
      } else {
        // Create new student
        const { data: newStudentId, error: createError } = await supabase
          .rpc('create_student_with_parent', {
            p_first_name: formData.firstName,
            p_last_name: formData.lastName,
            p_date_of_birth: formData.dateOfBirth,
            p_relationship: formData.relationship,
            p_metadata: {
              school: formData.school,
              grade: formData.grade,
              room_number: toStoredRoomNumber(formData.roomNumber),
              interested_programs: formData.interestedPrograms
            }
          });

        if (createError) throw createError;

        // Track successful student creation with details
        trackEvent({
          eventType: 'student_create',
          user: user,
          userType: 'parent',
          entityType: 'student',
          success: true,
          metadata: {
            action: 'student_created',
            student_first_name: formData.firstName,
            student_last_name: formData.lastName,
            student_date_of_birth: formData.dateOfBirth,
            student_school: formData.school,
            student_grade: formData.grade,
            student_interested_programs: formData.interestedPrograms,
            relationship: formData.relationship,
            status: 'completed'
          }
        });

        // Invalidate now so the list is fresh in the background
        await queryClient.invalidateQueries({ queryKey: ['students-list'] });

        if (newStudentId) {
          setCreatedStudentId(newStudentId as string);
          setCreatedStudentFirstName(formData.firstName);
          setInviteStep(true);
          setIsSubmitting(false);
          return;
        }

        onClose();
        return;
      }

      // Invalidate and refetch students list
      await queryClient.invalidateQueries({ queryKey: ['students-list'] });
      onClose();
    } catch (err) {
      // Track failed student creation
      if (!isEdit) {
        trackEvent({
          eventType: 'student_create',
          user: user,
          userType: 'parent',
          entityType: 'student',
          success: false,
          errorMessage: getErrorMessage(err),
          metadata: {
            action: 'student_creation_failed',
            student_first_name: formData.firstName,
            student_last_name: formData.lastName,
            student_date_of_birth: formData.dateOfBirth,
            student_school: formData.school,
            student_grade: formData.grade,
            student_interested_programs: formData.interestedPrograms,
            relationship: formData.relationship,
            status: 'failed'
          }
        });
      }
      setError(err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'add'} child`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendInvite = async () => {
    if (!createdStudentId || !inviteEmail.trim()) return;
    setInviteStatus({ tag: 'sending' });
    try {
      const { data, error } = await supabase.functions.invoke('invite-co-parent', {
        body: { student_id: createdStudentId, invitee_email: inviteEmail.trim().toLowerCase() },
      });
      if (error) {
        const fnErr = error as FnError;
        let msg = 'Failed to send invite. Please try again.';
        if (fnErr?.context?.json) {
          try { const body = await fnErr.context.json(); msg = body.message ?? msg; } catch { /* ignore */ }
        } else if (fnErr.message) {
          msg = fnErr.message;
        }
        setInviteStatus({ tag: 'error', message: msg });
        return;
      }
      const result = data as InviteResult;
      setInviteStatus({ tag: 'done', resultType: result.type });
    } catch (err) {
      setInviteStatus({ tag: 'error', message: err instanceof Error ? err.message : 'Failed to send invite.' });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (programType: string) => {
    setFormData(prev => {
      const nextInterested = prev.interestedPrograms.includes(programType)
        ? prev.interestedPrograms.filter(p => p !== programType)
        : [...prev.interestedPrograms, programType];

      if (isEdit) {
        // No validation in edit mode
        setProgramsError(false);
      } else {
        // In create mode: show error when none selected; clear when at least one selected
        setProgramsError(nextInterested.length === 0);
      }

      return {
        ...prev,
        interestedPrograms: nextInterested
      };
    });
  };

  // Invite step overlay (shown after successful student creation)
  if (inviteStep) {
    const inviteEmailNormalized = inviteEmail.trim().toLowerCase();
    const emailValid = inviteEmailNormalized.includes('@') && inviteEmailNormalized.includes('.');

    return (
      <div className="fixed inset-0 z-[60] flex flex-col justify-end sm:items-center sm:justify-center">
        <div className="absolute inset-0 bg-gray-500/75" aria-hidden="true" />
        <div className="relative w-full bg-white rounded-t-2xl shadow-xl sm:rounded-lg sm:max-w-md sm:my-8 flex flex-col max-h-[90vh]">
          <div className="flex justify-center pt-3 pb-1 sm:hidden" aria-hidden="true">
            <div className="w-10 h-1 rounded-full bg-gray-300" />
          </div>
          <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-200">
            <Users className="w-4 h-4 text-blue-600" />
            <h2 className="text-base font-semibold text-gray-900">{t('action.inviteCoParent')}</h2>
          </div>
          <div className="overflow-y-auto p-5" style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom, 0px))' }}>
          <p className="text-sm text-gray-500 mb-5">
            {createdStudentFirstName} has been added! Would you like to share access with a co-parent?
          </p>

          {inviteStatus.tag === 'done' ? (
            <div className="text-center py-2">
              <p className="text-sm font-medium text-green-700 mb-1">
                {inviteStatus.resultType === 'access_granted' ? 'Access granted!' : 'Invite sent!'}
              </p>
              <p className="text-sm text-gray-600 mb-5">
                {inviteStatus.resultType === 'access_granted'
                  ? `${inviteEmailNormalized} can now manage ${createdStudentFirstName}'s enrollment.`
                  : `An invite was emailed to ${inviteEmailNormalized}.`}
              </p>
              <button onClick={onClose} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                Done
              </button>
            </div>
          ) : (
            <>
              {inviteStatus.tag === 'error' && (
                <p className="mb-3 text-sm text-red-600">{inviteStatus.message}</p>
              )}
              <div className="flex gap-2 mb-5">
                <div className="flex-1 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={e => { setInviteEmail(e.target.value); if (inviteStatus.tag === 'error') setInviteStatus({ tag: 'idle' }); }}
                    placeholder="co-parent@example.com"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={inviteStatus.tag === 'sending'}
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Skip
                </button>
                <button
                  onClick={handleSendInvite}
                  disabled={!emailValid || inviteStatus.tag === 'sending'}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {inviteStatus.tag === 'sending' ? 'Sending…' : 'Send Invite'}
                </button>
              </div>
            </>
          )}
          </div>{/* overflow-y-auto body */}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end sm:items-center sm:justify-center">
      <div className="absolute inset-0 bg-gray-500/75" aria-hidden="true" />
      <div className="relative w-full bg-white rounded-t-2xl shadow-xl sm:rounded-lg sm:max-w-5xl sm:my-8 flex flex-col max-h-[90vh]">
        <div className="flex justify-center pt-3 pb-1 sm:hidden" aria-hidden="true">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">{isEdit ? 'Edit Child' : 'Add New Child'}</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 p-1.5 rounded" aria-label="Close">
            <span aria-hidden className="text-xl leading-none">×</span>
          </button>
        </div>
        <div className="overflow-y-auto p-6" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 0px))' }}>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information Section */}
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    required
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                {!isEdit && (
                  <div>
                    <label htmlFor="relationship" className="block text-sm font-medium text-gray-700">
                      Relationship
                    </label>
                    <select
                      id="relationship"
                      name="relationship"
                      value={formData.relationship}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="Parent">Parent</option>
                      <option value="Guardian">Guardian</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Student Profile Section. Both of its fields are brand-declared, so the panel
                itself disappears for a brand that asks for neither rather than rendering a
                heading over nothing. */}
            {(intake.collectsSchoolAndGrade || intake.collectsRoomNumber) && (
            <div className="bg-white rounded-lg shadow p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Student Profile</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">Additional information about your child</p>
              <div className="space-y-4">
                {intake.collectsSchoolAndGrade && (
                  <>
                  <div>
                    <label htmlFor="school" className="block text-sm font-medium text-gray-700">
                      School (Current School Year) <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="school"
                      name="school"
                      value={formData.school}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Select a school</option>
                      {schoolsList.map((school: string) => (
                        <option key={school} value={school}>
                          {school}
                        </option>
                      ))}
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {formData.school === 'Other' && (
                    <div>
                      <label htmlFor="otherSchool" className="block text-sm font-medium text-gray-700">
                        Other School Name
                      </label>
                      <input
                        type="text"
                        id="otherSchool"
                        name="school"
                        value={formData.school}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Enter school name"
                      />
                    </div>
                  )}

                  <div>
                    <label htmlFor="grade" className="block text-sm font-medium text-gray-700">
                      Grade (Current School Year) <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="grade"
                      name="grade"
                      value={formData.grade}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Select a grade</option>
                      {grades.map((grade) => (
                        <option key={grade.value} value={grade.value}>
                          {grade.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  </>
                )}

                {intake.collectsRoomNumber && (
                  <>
                  <div>
                    <label htmlFor="roomNumber" className="block text-sm font-medium text-gray-700">
                      Classroom Room Number
                    </label>
                    {/* Deliberately type="text" with a numeric keypad rather than
                        type="number": a number input would drop the leading zero from a
                        room written as "013", and would accept "1e5" and "-2". */}
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="off"
                      id="roomNumber"
                      name="roomNumber"
                      value={formData.roomNumber}
                      onChange={(e) =>
                        setFormData({ ...formData, roomNumber: sanitizeRoomNumberInput(e.target.value) })
                      }
                      placeholder="e.g. 013"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Optional. Digits only — helps teachers group students at pickup.
                    </p>
                  </div>
                  </>
                )}

              </div>
            </div>
            )}
          </div>

          {/* Interested Programs Section */}
          <div className="mt-6 bg-white rounded-lg shadow p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Interested Programs</h3>
              {programsError && (
                <span className="text-sm text-red-600">Please select at least one</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mb-4">Let us know what types of programs your child might be interested in</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {intake.interests.map((interest) => (
                <div key={interest.value} className="flex items-center">
                  <input
                    type="checkbox"
                    id={interest.value}
                    checked={formData.interestedPrograms.includes(interest.value)}
                    onChange={() => handleCheckboxChange(interest.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor={interest.value} className="ml-3 text-sm font-medium text-gray-700">
                    {interest.icon} {interest.label}
                  </label>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              This helps us provide better recommendations and plan our program offerings
            </p>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isSubmitting ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save' : 'Add Child')}
            </button>
          </div>
        </form>
        </div>{/* overflow-y-auto body */}
      </div>
    </div>
  );
}

// For backward compatibility
export function AddChildForm(props: ChildFormProps) {
  return <ChildForm {...props} />;
} 